/**
 * ClarificationModal — ein Termin „in Klärung“ (#351) bekommt eine neue
 * Frist (`mode="deadline"`) oder sein festes Datum (`mode="plan"`). Beides
 * ändert denselben Datensatz; der Verlauf steht im Audit.
 */
import { Alert, Button, Group, Modal, Stack, Text } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useUpdateVisit } from "../../hooks/useVisits.js";
import { formatIsoDate, fromLocalDateString, toLocalDateString } from "../../lib/date.js";
import { ClarifyDeadlineInput } from "./ClarifyDeadlineInput.js";

export function ClarificationModal({
  opened,
  onClose,
  mode,
  visitId,
  clarifyUntil,
  customerName,
}: {
  opened: boolean;
  onClose: () => void;
  mode: "deadline" | "plan";
  visitId: string;
  clarifyUntil: string;
  customerName: string;
}) {
  const { t } = useTranslation();
  const update = useUpdateVisit(visitId);
  const [datum, setDatum] = useState<Date | null>(
    mode === "deadline" ? fromLocalDateString(clarifyUntil) : null,
  );
  const [fehler, setFehler] = useState<string | null>(null);

  async function speichern() {
    if (!datum) {
      setFehler(mode === "deadline" ? t("pages.visits.clarify.deadlineError") : t("pages.visits.createModal.dateError"));
      return;
    }
    const tag = toLocalDateString(datum);
    if (mode === "deadline" && tag < toLocalDateString(new Date())) {
      setFehler(t("pages.visits.clarify.deadlinePastError"));
      return;
    }
    setFehler(null);
    try {
      await update.mutateAsync(mode === "deadline" ? { clarifyUntil: tag } : { status: "planned", scheduledAt: tag });
      onClose();
    } catch {
      // Anzeige über update.error
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={mode === "deadline" ? t("pages.visits.clarify.deadlineTitle") : t("pages.visits.clarify.planTitle")}
      size="sm"
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        <Stack gap={4}>
          <Text size="sm">
            <Text span fw={600}>{t("pages.visits.customerLabel")} </Text>
            {customerName}
          </Text>
          <Text size="sm">
            <Text span fw={600}>{t("pages.visits.clarify.deadlineCurrent")} </Text>
            {formatIsoDate(clarifyUntil)}
          </Text>
        </Stack>

        {mode === "deadline" ? (
          <ClarifyDeadlineInput value={datum} onChange={setDatum} error={fehler ?? undefined} />
        ) : (
          <>
            <Text size="sm" c="dimmed">
              {t("pages.visits.clarify.planDescription")}
            </Text>
            <DatePickerInput
              label={t("pages.visits.clarify.planDate")}
              withAsterisk
              placeholder={t("pages.visits.createModal.datePlaceholder")}
              value={datum}
              onChange={setDatum}
              error={fehler ?? undefined}
              description={
                datum && toLocalDateString(datum) < toLocalDateString(new Date())
                  ? t("pages.visits.createModal.datePastHint")
                  : undefined
              }
              locale="de"
            />
          </>
        )}

        {update.isError && (
          <Alert color="red" title={t("common.error")}>
            {update.error instanceof Error ? update.error.message : t("pages.visits.clarify.saveError")}
          </Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={update.isPending}>
            {t("common.cancel")}
          </Button>
          <Button loading={update.isPending} onClick={() => void speichern()}>
            {mode === "deadline" ? t("pages.visits.clarify.deadlineSubmit") : t("pages.visits.clarify.planSubmit")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
